import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../../../common/Forms/Input/Input';
import { notify } from '../../../../assets/helpers/toast';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as schemas from '../../../../assets/schemas/schemas';
import { ActionButton, AltButton } from '../../../common/Buttons/Buttons';
import {
  idCodes,
  genderOptions,
  civilStateOptions,
  documentUploadTypeOptions,
  documentUploadType,
  documentTypeArr,
  codeTypeEnum,
  gendersEnum,
} from '../../../../assets/helpers/options';
import { useParams } from 'react-router-dom';
import { broadCastService } from '../../../../services/broadCastService';
import { customerService } from '../../../../services/customerService';
import { membershipService } from '../../../../services/membershipService';
import { LoadSpinner } from '../../../common/LoadSpinner/LoadSpinner';
import {
  SalesDropzone,
  FileComponent,
} from '../../../common/Dropzone/Dropzone';
import { useDropzone } from 'react-dropzone';
import {
  capitalize,
  filterParams,
  filterOption,
  validateAndSet,
  mergeObjects,
  convertToBase64,
  mergeObjectValues,
  mapCivilStateToEnum,
} from '../../../../assets/helpers/funciones';
import {
  idEnums,
  documentTypeEnum,
  idDocumentTypeOptions,
} from '../../../../assets/helpers/options';
import {
  preloadPersonalData,
  handleTestImageUpload,
} from './helpers/preloadFuntions';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { MultipleVehiclesTable } from '../MultipleAfiliationComponents/MultipleVehiclesTable';
import moment from 'moment';
import { multipleVehiclesTableHeader } from '../../../../assets/helpers/tables';
import authService from '../../../../services/authService';
import checkbox from '../../../../images/checkbox.svg';
import checkboxChecked from '../../../../images/checkboxChecked.svg';
import './LandingForms.scss';
import { parseDateFlexible } from '../../../../assets/helpers/parsers';

export const MultipleAffiliationForm = React.memo((props) => {
  const {
    setcurentStep,
    membership,
    membershipList,
    setMembershipList,
    setCustomer,
    curentStep,
  } = props;
  const t = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
    reset,
  } = useForm({
    resolver: yupResolver(schemas.CreateCustomerSchema(t)),
  });

  const [idUploaded, setIdUploaded] = useState('');
  const [isLoading, setIsloading] = useState(false);
  const [clientNumber, setClientNumber] = useState(-1);
  const [isSameClient, setIsSameClient] = useState(false);
  const { saleChannel } = useParams();

  const [broadcastOptions, setBroadcastOptions] = useState([]);

  const formatResponse = (dataObj, documentType) => {
    if (!dataObj || !documentType) return {};

    // Candidate key forms: original, UPPER, camel->snake, snake->camel
    const candidates = [
      documentType,
      documentType.toUpperCase(),
      documentType.replace(/([A-Z])/g, '_$1').toLowerCase(),
      documentType.replace(/[_-](\w)/g, (_, c) => c.toUpperCase()),
    ];

    for (const k of candidates) {
      if (k && Object.prototype.hasOwnProperty.call(dataObj, k)) {
        return dataObj[k] || {};
      }
    }

    // Fallback: ignore separators and case (drivinglicense vs driving_license)
    const normalized = documentType.replace(/[_-]/g, '').toLowerCase();
    for (const key of Object.keys(dataObj)) {
      if (key && key.replace(/[_-]/g, '').toLowerCase() === normalized) {
        return dataObj[key] || {};
      }
    }

    return {};
  };

  const parseMultipleDocs = (str) => {
    if (!str || typeof str !== 'string')
      return { flags: {}, hasVehicle: false };
    const props = str
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p);

    const flags = props.reduce((obj, prop) => {
      obj[prop] = true;
      return obj;
    }, {});

    const VEHICLE_DOC_VALUES = [
      documentTypeEnum.CERTIFICATE_OR_TITLE,
      documentTypeEnum.CIRCULATION_CARD,
      documentTypeEnum.ORIGIN_CERTIFICATE,
      documentTypeEnum.PURCHASE_DOCUMENT,
    ];

    const hasVehicle = props.some((p) => VEHICLE_DOC_VALUES.includes(p));

    return { flags, hasVehicle };
  };

  // Returns true when multiple selection lacks an ID doc (cedula/passport/rif) paired with another doc
  const isInvalidMultiDocSelection = (str) => {
    const { flags } = parseMultipleDocs(str);
    const selected = Object.keys(flags);
    if (selected.length < 2) return false;

    const idDocs = [
      documentTypeEnum.CEDULA,
      documentTypeEnum.PASSPORT,
      documentTypeEnum.RIF,
    ];

    const hasIdDoc = selected.some((doc) => idDocs.includes(doc));
    const hasNonIdDoc = selected.some((doc) => !idDocs.includes(doc));

    return !(hasIdDoc && hasNonIdDoc);
  };

  const getDocumentsData = async (file, documentType, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (showUploader()) {
      const allSelected = showUploader();
      let multipleDocs = allSelected;

      // If caller forces an ID doc, prepend it when missing
      if (options?.forceDocumentType) {
        const arr = multipleDocs
          ? multipleDocs.split(',').map((s) => s.trim())
          : [];
        if (!arr.includes(options.forceDocumentType)) {
          multipleDocs = `${options.forceDocumentType}, ${multipleDocs}`;
        }
      }

      formData.append('documentType', multipleDocs);

      const parsed = parseMultipleDocs(multipleDocs);
      if (file && parsed.hasVehicle) {
        const base64 = await convertToBase64(file);
        sessionStorage.setItem('idUploaded', base64);
      }
      setIsloading(true);
      membershipService
        .getDocumentsDataV2(formData)
        .then((response) => {
          if ([200, 201].includes(response.status)) {
            const parsed = parseMultipleDocs(multipleDocs);
            const parsedFlags = parsed.flags;
            let res = mergeObjectValues(response?.data) || {};
            // merge specific sub-objects for each selected key (handles snake_case, camelCase...)
            for (const docKey of Object.keys(parsedFlags)) {
              const part = formatResponse(response?.data || {}, docKey);
              if (part && Object.keys(part).length > 0) {
                res = { ...res, ...part };
              }
            }
            if (parsed.hasVehicle) {
              localStorage.setItem(
                'documentsInfo',
                JSON.stringify({ ...res, ...parsedFlags })
              );
            }
            if (res && documentType === documentTypeEnum?.PASSPORT) {
              validateAndSet('identificationType', idEnums?.P, setValue, watch);
              validateAndSet(
                'identification',
                res?.passportNumber,
                setValue,
                watch
              );
              validateAndSet(
                'lastName',
                capitalize(res?.lastNames),
                setValue,
                watch
              );
              validateAndSet(
                'firstName',
                capitalize(res?.names),
                setValue,
                watch
              );
            }

            if (res && res?.rif) {
              validateAndSet('identificationType', idEnums?.J, setValue, watch);
              validateAndSet(
                'identification',
                // replace J/G/R if present
                res?.rif?.replace(/[JGRV]/g, ''),
                setValue,
                watch
              );
              validateAndSet('firstName', res?.enterpriseName, setValue, watch);
              validateAndSet(
                'birthday',
                res?.registrationDate
                  ? moment(res?.registrationDate, 'DD/MM/YYYY').toDate()
                  : '',
                setValue,
                watch
              );
            }
            if (watch('identificationType') !== idEnums?.J) {
              validateAndSet(
                'firstName',
                capitalize(res?.names),
                setValue,
                watch
              );
              validateAndSet(
                'lastName',
                capitalize(res?.lastNames),
                setValue,
                watch
              );
            }
            if (res?.civilState) {
              validateAndSet(
                'maritalStatus',
                mapCivilStateToEnum(res?.civilState),
                setValue,
                watch
              );
            }
            validateAndSet(
              'birthday',
              parseDateFlexible(res?.birthday ?? res?.birthDate),
              setValue,
              watch
            );
            validateAndSet(
              'identificationType',
              capitalize(res?.nationality),
              setValue,
              watch
            );
            if (!watch('identification')) {
              validateAndSet(
                'identification',
                res?.identification?.toString().replace(/\D/g, '') || '',
                setValue,
                watch
              );
            }
            validateAndSet(
              'gender',
              res?.gender === 'M' || res?.gender === 'MASCULINO'
                ? gendersEnum.MALE
                : res?.gender === 'F' || res?.gender === 'FEMENINO'
                  ? gendersEnum.FEMALE
                  : gendersEnum.OTHER,
              setValue,
              watch
            );
            if (
              watch('identificationType') &&
              watch('identification') &&
              watch('identification').length > 5
            ) {
              customerService
                .getCustomerById(
                  watch('identification'),
                  watch('identificationType')
                )
                .then((response) => {
                  if (response.status === 200) {
                    if (
                      response?.data?.customer?.matchedMemberships?.length > 0
                    ) {
                      notify(
                        `Este cliente ya tiene una afiliación activa.`,
                        'info'
                      );
                    }
                  }
                })
                .catch((error) => {});
            }
            setIsloading(false);
          } else {
            setIsloading(false);
            notify(`${response.message}` || 'error', 'error');
          }
        })
        .catch((error) => {
          setIsloading(false);
        });
    } else {
      formData.append(
        'documentType',
        options?.forceDocumentType || documentType
      );
      setIsloading(true);
      membershipService
        .getDocumentsDataV2(formData)
        .then((response) => {
          if ([200, 201].includes(response.status)) {
            const dataObj = response?.data || {};
            let res =
              formatResponse(dataObj, documentType) ||
              response?.data?.drivingLicense ||
              {};
            if (res && documentType === documentTypeEnum?.PASSPORT) {
              res = response?.data?.passport || dataObj;
              validateAndSet('identificationType', idEnums?.P, setValue, watch);
              validateAndSet(
                'identification',
                res?.passportNumber,
                setValue,
                watch
              );
              validateAndSet(
                'lastName',
                capitalize(res?.lastNames),
                setValue,
                watch
              );
              validateAndSet(
                'firstName',
                capitalize(res?.names),
                setValue,
                watch
              );
            }

            if (response?.data && documentType === documentTypeEnum?.RIF) {
              validateAndSet('identificationType', idEnums?.J, setValue, watch);
              validateAndSet(
                'identification',
                res?.rif?.replace(/[JGRV]/g, ''),
                setValue,
                watch
              );
              validateAndSet('firstName', res?.enterpriseName, setValue, watch);
              validateAndSet(
                'birthday',
                res?.registrationDate
                  ? moment(res?.registrationDate, 'DD/MM/YYYY').toDate()
                  : '',
                setValue,
                watch
              );
            }
            if (watch('identificationType') !== idEnums?.J) {
              validateAndSet(
                'firstName',
                capitalize(res?.names),
                setValue,
                watch
              );
              validateAndSet(
                'lastName',
                capitalize(res?.lastNames),
                setValue,
                watch
              );
              validateAndSet(
                'birthday',
                parseDateFlexible(res?.birthday ?? res?.birthDate),
                setValue,
                watch
              );
            }
            if (
              documentType === documentTypeEnum?.CEDULA &&
              res?.civilState !== null &&
              res?.civilState !== undefined
            ) {
              validateAndSet(
                'maritalStatus',
                mapCivilStateToEnum(res?.civilState),
                setValue,
                watch
              );
            }
            validateAndSet(
              'identificationType',
              capitalize(
                res?.nationality && documentType !== documentTypeEnum?.PASSPORT
                  ? res?.nationality
                  : watch('identificationType')
              ),
              setValue,
              watch
            );
            if (!watch('identification')) {
              validateAndSet(
                'identification',
                res?.identification?.toString().replace(/\D/g, '') || '',
                setValue,
                watch
              );
            }
            validateAndSet(
              'gender',
              res?.gender === 'M' || res?.gender === 'MASCULINO'
                ? gendersEnum.MALE
                : res?.gender === 'F' || res?.gender === 'FEMENINO'
                  ? gendersEnum.FEMALE
                  : gendersEnum.OTHER,
              setValue,
              watch
            );

            if (
              response?.data &&
              documentType === response?.data?.drivingLicense
            ) {
              validateAndSet(
                'birthDate',
                parseDateFlexible(res?.birthday),
                setValue,
                watch
              );
            }
            if (
              watch('identificationType') &&
              watch('identification') &&
              watch('identification').length > 5
            ) {
              customerService
                .getCustomerById(
                  watch('identification'),
                  watch('identificationType')
                )
                .then((response) => {
                  if (response.status === 200) {
                    if (
                      response?.data?.customer?.matchedMemberships?.length > 0
                    ) {
                      notify(
                        `Este cliente ya tiene una afiliación activa.`,
                        'info'
                      );
                    }
                  }
                })
                .catch((error) => {});
            }
            setIsloading(false);
          } else {
            setIsloading(false);
            notify(`${response.message}` || 'error', 'error');
          }
        })
        .catch((error) => {
          setIsloading(false);
        });
    }
  };

  const onDrop1 = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach(async (file) => {
        const reader = new FileReader();

        reader.onload = () => {
          // Do whatever you want with the file contents
        };
        reader.readAsArrayBuffer(file);

        if (
          !['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'].includes(
            file.type
          )
        ) {
          notify('Agregar un formato valido de imagen', 'error');
          return null;
        }
        let maxWeight = 5;
        if (file.size * 0.000001 > maxWeight) {
          notify(`La imagen no debe superar los ${maxWeight} MB`, 'error');
        } else {
          setIdUploaded(file);

          const invalidMulti = isInvalidMultiDocSelection(showUploader());
          const forcedIdDoc = invalidMulti
            ? watch('identificationFileDocType')
            : undefined;

          await getDocumentsData(file, watch('documentTypeImage'), {
            forceDocumentType: forcedIdDoc,
          });
        }
      });
    },
    [documentTypeEnum, watch]
  );

  const vehicleDataMultipleCheck = (str) => {
    const { flags, hasVehicle } = parseMultipleDocs(str);
    return hasVehicle ? flags : false;
  };

  const showUploader = () => {
    const allDocumentTypes = Object.values(documentTypeEnum);
    const trueDocuments = allDocumentTypes.filter((docName) => {
      return watch(docName);
    });
    if (trueDocuments.length > 0) {
      return trueDocuments.join(', ');
    }
    return false;
  };

  const getCustomerById = () => {
    setValue('identification', watch('identification').replace(/\s/g, ''));

    if (
      watch('identificationType') &&
      watch('identification') &&
      watch('identification').length > 5
    ) {
      customerService
        .getCustomerById(watch('identification'), watch('identificationType'))
        .then((response) => {
          if (response.status === 200) {
            const {
              firstName,
              lastName,
              gender,
              maritalStatus,
              birthday,
              email,
              state,
              phonePrefix,
              broadcastChannel,
              instagram,
            } = response?.data?.customer;

            setValue('firstName', firstName);
            setValue('instagram', instagram);
            setValue('lastName', lastName);
            if (gender) {
              setValue('gender', gender);
            }
            if (maritalStatus) {
              setValue('maritalStatus', maritalStatus);
            }
            if (birthday) {
              setValue('birthday', new Date(birthday));
            }
            if (state) {
              setValue('state', state);
              Promise.allSettled([getBroadcastChannels()]).then((r) => {
                setTimeout(() => {
                  setValue('broadcastChannel', broadcastChannel);
                }, 1000);
              });
            }
            setValue('email', email?.toLowerCase());
            setValue('phonePrefix', phonePrefix);

            sessionStorage.setItem(
              'customer',
              JSON.stringify(response?.data?.customer)
            );
            if (response?.data?.customer?.matchedMemberships?.length > 0) {
              notify(`Este cliente ya tiene una afiliación activa.`, 'info');
            }
          }
          if (response.status === 404) {
            setValue('firstName', '');
            setValue('lastName', '');
            setValue('gender', '');
            setValue('birthday', '');
            setValue('state', '');
            setValue('broadcastChannel', '');
            setValue('email', '');
            setValue('phonePrefix', '');
            setValue('phoneNumber', '');
            setValue('instagram', '');
          }
        })
        .catch((error) => {});
    }
  };

  const validateUser = (user) => {
    let dataToPost = { ...user };
    dataToPost.membership = membershipList[clientNumber]?.membership;
    // Load document booleans
    dataToPost.hasDni = idUploaded?.name ? true : false;

    // Validate user then
    if (!isLoading) {
      setIsloading(true);
      customerService
        .validateCustomer(dataToPost)
        .then((response) => {
          if ([200, 201].includes(response.status)) {
            setIsloading(false);

            let customer = dataToPost;
            let data = [...membershipList];

            // Document loads
            data[clientNumber].dataTable.dni = idUploaded;

            data[clientNumber].customer = customer;
            data[clientNumber].dataTable.documentTypeImage =
              watch('documentTypeImage');
            setIdUploaded('');
            setClientNumber(-1);
            reset();

            if (isSameClient && clientNumber === 0) {
              sameClient();
            }
          } else {
            setIsloading(false);
            notify(`${response.message}` || 'error', 'error');
          }
        })
        .catch((error) => {
          setIsloading(false);
        });
    }
  };

  const addClient = (index) => {
    setClientNumber(index);
  };

  const editClient = (index) => {
    // Add documents
    setClientNumber(index);
    setValue(
      'documentTypeImage',
      membershipList[index].dataTable?.documentTypeImage
    );
    setIdUploaded(membershipList[index].dataTable?.dni);

    setValue(
      'identificationType',
      membershipList[index].customer?.identificationType
    );
    setValue('identification', membershipList[index].customer?.identification);
    setValue('email', membershipList[index].customer?.email);
    setValue('optionalEmail', membershipList[index].customer?.optionalEmail);
    setValue('firstName', membershipList[index].customer?.firstName);
    setValue('lastName', membershipList[index].customer?.lastName);
    setValue(
      'phone',
      membershipList[index].customer?.phonePrefix +
        membershipList[index].customer?.phoneNumber
    );
    setValue(
      'optionalPhone',
      membershipList[index].customer?.optionalPhonePrefix +
        membershipList[index].customer?.optionalPhoneNumber
    );
    setValue(
      'birthday',
      membershipList[index].customer?.birthday
        ? new Date(
            moment(membershipList[index].customer?.birthday)?.format(
              'ddd MMM DD YYYY 04:mm:ss ZZ'
            )
          )
        : ''
    );
    setValue('gender', membershipList[index].customer?.gender);
    setValue('maritalStatus', membershipList[index].customer?.maritalStatus);
    setValue(
      'broadcastChannel',
      membershipList[index].customer?.broadcastChannel
    );
    setValue(
      'broadcastChannelDescription',
      membershipList[index].customer?.broadcastChannelDescription
    );
    setValue('instagram', membershipList[index].customer?.instagram);
  };

  const onSubmit = handleSubmit(async (data) => {
    data.firstName = capitalize(data?.firstName);
    data.lastName = capitalize(data?.lastName);
    data.email = data?.email?.toLowerCase();
    data.birthday = moment(data.birthday).format('YYYY-MM-DD');

    if (data.phone) {
      const parsedPhone = parsePhoneNumberWithError(data.phone);
      data.phoneNumber = parsedPhone.nationalNumber;
      data.phonePrefix = '+' + parsedPhone.countryCallingCode;
    }

    if (data.optionalPhone) {
      const parsedOptionalPhone = parsePhoneNumberWithError(data.optionalPhone);
      data.optionalPhoneNumber = parsedOptionalPhone.nationalNumber;
      data.optionalPhonePrefix = '+' + parsedOptionalPhone.countryCallingCode;
    }

    // No birthday for J, R, G identifications
    if (
      [idEnums?.J, idEnums?.R, idEnums?.G].includes(watch('identificationType'))
    ) {
      delete data.birthday;
    }

    delete data?.documentTypeImage;
    delete data?.countryCode;
    delete data?.phone;
    delete data?.optionalPhone;
    delete data?.[documentTypeEnum.PASSPORT];
    delete data?.[documentTypeEnum.RIF];
    delete data?.[documentTypeEnum.CERTIFICATE_OR_TITLE];
    delete data?.[documentTypeEnum.CIRCULATION_CARD];
    delete data?.[documentTypeEnum.ORIGIN_CERTIFICATE];
    delete data?.[documentTypeEnum.PURCHASE_DOCUMENT];
    delete data?.[documentTypeEnum.CEDULA];
    delete data?.uploadType;
    delete data?.identificationFileDocType;

    let fileTopost = appendFile(filterParams(data));
    let user = { ...data };

    if (authService.getUser()) {
      validateUser(filterParams(data));
      return null;
    }

    if (!isLoading) {
      setIsloading(true);
      customerService
        .createCustomer(fileTopost)
        .then((response) => {
          if ([200, 201].includes(response.status)) {
            setIsloading(false);
            sessionStorage.setItem('personalData', JSON.stringify(user));
            setCustomer(response?.data?._id);
            setcurentStep(3);
          } else {
            setIsloading(false);
            notify(`${response.message}` || 'error', 'error');
          }
        })
        .catch((error) => {
          setIsloading(false);
        });
    }
  });

  const getBroadcastChannels = () => {
    broadCastService
      .getBroadcastChannels()
      .then((response) => {
        if (response.status === 200) {
          setBroadcastOptions(response?.data?.broadcastChannels);
        } else {
          notify(`${response.message}` || 'error', 'error');
        }
      })
      .catch((error) => {});
  };

  const appendFile = (data) => {
    const formData = new FormData();
    for (const key in data) {
      if (data[key]) {
        formData.append(key, data[key]);
      }
    }

    formData.append('membership', membership?._id);

    if (saleChannel) {
      formData.append('landing', 'Marqueting qr');
    }
    if (idUploaded) {
      formData.append('dni', idUploaded);
    }

    return formData;
  };

  const prelaodData = () => {
    let personalData = sessionStorage.getItem('personalData');
    personalData = JSON.parse(personalData);
    if (personalData) {
      setValue('firstName', personalData?.firstName);
      setValue('lastName', personalData?.lastName);
      setValue('phonePrefix', personalData?.phonePrefix);
      setValue('phoneNumber', personalData?.phoneNumber);
      setValue('identificationType', personalData?.identificationType);
      setValue('identification', personalData?.identification);
      setValue('email', personalData?.email);
      setValue(
        'birthday',
        new Date(
          moment(personalData?.birthday)?.format('ddd MMM DD YYYY 04:mm:ss ZZ')
        )
      );
      setValue('identificationType', personalData?.identificationType);
      setValue('gender', personalData?.gender);
      setValue('maritalStatus', personalData?.maritalStatus);

      Promise.allSettled([getBroadcastChannels()]).then((r) => {
        setTimeout(() => {
          setValue('broadcastChannel', personalData?.broadcastChannel);
        }, 500);
      });

      setValue('identificationType', personalData?.identificationType);
    } else {
      getBroadcastChannels();
    }
  };

  const onChangeUploadType = () => {
    setIdUploaded('');
    setValue('documentTypeImage', '');
    setValue(documentTypeEnum.PASSPORT, false);
    setValue(documentTypeEnum.RIF, false);
    setValue(documentTypeEnum.CERTIFICATE_OR_TITLE, false);
    setValue(documentTypeEnum.CIRCULATION_CARD, false);
    setValue(documentTypeEnum.ORIGIN_CERTIFICATE, false);
    setValue(documentTypeEnum.PURCHASE_DOCUMENT, false);
    setValue(documentTypeEnum.CEDULA, false);
    setValue('firstName', '');
    setValue('lastName', '');
    setValue('phone', '');
    setValue('enamil', '');
    setValue('optionalEmail', '');
    setValue('identificationType', '');
    setValue('identification', '');
    setValue('birthday', '');
    setValue('gender', '');
    setValue('maritalStatus', '');
    setValue('instagram', '');
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop: onDrop1 });

  const editCustomer = () => {
    let clientIndex = sessionStorage.getItem('clientIndex');
    if (clientIndex) {
      editClient(parseFloat(clientIndex));
    }
  };

  const nextStep = () => {
    sessionStorage.removeItem('clientIndex');
    // If return to step 1 new memberships are created, they need to be asociated to the new customer
    let data = [...membershipList];
    for (let i = 0; i < data.length; i++) {
      data[i].customer.membership = membershipList[i]?.membership;
    }

    setMembershipList(data);
    setcurentStep(3);
  };

  useEffect(() => {
    editCustomer();
    prelaodData();

    //check if in localstorage there's documents info and if exist delete it
    if (localStorage.getItem('documentsInfo')) {
      localStorage.removeItem('documentsInfo');
    }

    // check if in sessionstorage there's id uploaded and if exist delete it
    if (sessionStorage.getItem('idUploaded')) {
      sessionStorage.removeItem('idUploaded');
    }
  }, []);

  let validateDisabled = () => {
    return !watch('identificationType') || !watch('identification');
  };

  let validateIfshowForm = () => {
    if (saleChannel) {
      return true;
    }
    if (watch('documentTypeImage')) {
      return true;
    } else return false;
  };

  const preloadPage = () => {
    handleTestImageUpload(
      onDrop1,
      `${process.env.PUBLIC_URL}/CedulaPrueba.jpeg`,
      'CedulaPrueba.jpeg'
    );
    preloadPersonalData(setValue);
  };

  const validateButton = () => {
    let label = '';

    if (membershipList[clientNumber]?.customer) {
      label = 'Editar cliente';
    } else {
      label = 'Agregar cliente';
    }

    if (clientNumber >= 0) {
      return (
        <ActionButton
          label={authService.getUser() ? label : 'Siguiente'}
          action={onSubmit}
        />
      );
    } else return null;
  };

  const validateContinueButton = () => {
    let canPass = true;

    for (let i = 0; i < membershipList.length; i++) {
      if (!membershipList[i].customer) {
        canPass = false;
      }
    }

    if (authService.getUser() && canPass && clientNumber < 0) {
      return <ActionButton label={'Confirmar'} action={() => nextStep()} />;
    } else return null;
  };

  const sameClient = () => {
    let data = [...membershipList];

    if (data[0].customer) {
      for (let i = 0; i < membershipList.length; i++) {
        let customer = { ...data[0].customer };
        data[i].customer = customer;
        data[i].customer.membership = membershipList[i]?.membership;
        data[i].dataTable.dni = data[0].dataTable.dni;
      }
      setMembershipList(data);
    }
    setIsSameClient(true);
  };

  const differentClient = () => {
    let data = [...membershipList];
    for (let i = 1; i < data.length; i++) {
      delete data[i].customer;
    }
    setMembershipList(data);
    setIsSameClient(false);
  };

  const multipleClientsTable = {
    header: multipleVehiclesTableHeader,
    data: membershipList,
    actions: { addClient: addClient, editClient: editClient },
    curentStep: curentStep,
    clientNumber: clientNumber,
  };

  return (
    <>
      <LoadSpinner show={isLoading} />
      <div className="landing-form-container">
        <h5
          onClick={
            ['true', true].includes(process.env.REACT_APP_DEVELOP)
              ? () => preloadPage()
              : ''
          }
        >
          Información Personal
        </h5>
        <p onClick={() => handleTestImageUpload()}>(*) Campos obligatorios</p>
        <br />
        <MultipleVehiclesTable table={multipleClientsTable} />
        <SameClientComponent
          sameClient={sameClient}
          differentClient={differentClient}
          isSameClient={isSameClient}
        />
        {(!authService.getUser() || clientNumber >= 0) && (
          <form className="responsive-landing-form">
            <div className="row">
              <div className="col-3"></div>
              <Input
                bootstrap={'col-12 col-md-6'}
                label={'Tipo de carga *'}
                type={'select'}
                defaultValue={documentUploadType.SIMPLE}
                capitalizeOff={true}
                name={'uploadType'}
                register={register}
                placeholder={''}
                options={documentUploadTypeOptions}
                noBlankOption={true}
                onChange={onChangeUploadType}
              />
              <div className="col-3"></div>

              {watch('uploadType') &&
              watch('uploadType') === documentUploadType.MULTIPLE ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        margin: '0 auto',
                        width: '100%',
                        gap: '5px',
                        justifyContent: 'center',
                        display: 'flex',
                      }}
                      className="row"
                    >
                      {documentTypeArr.map((item, index) => (
                        <Input
                          key={index}
                          bootstrap={'col-5 text-center'}
                          subLabel={item.name}
                          type={'checkbox'}
                          name={item._id}
                          placeholder={''}
                          register={register}
                          errors={errors?.km?.message}
                        />
                      ))}
                    </div>
                  </div>

                  {showUploader() && (
                    <>
                      {isInvalidMultiDocSelection(showUploader()) && (
                        <div className="col-12 col-md-12">
                          <Input
                            bootstrap={'col-md-6 mt-3'}
                            label={'Archivo de identificación *'}
                            type={'select'}
                            name={'identificationFileDocType'}
                            register={register}
                            capitalizeOff={true}
                            placeholder={''}
                            options={idDocumentTypeOptions}
                            errors={errors?.identificationFileDocType?.message}
                          />
                        </div>
                      )}
                      <div
                        className="col-12 col-md-6"
                        style={{
                          marginTop: '18px',
                        }}
                      >
                        {idUploaded ? (
                          <FileComponent
                            file={idUploaded}
                            remove={() => setIdUploaded('')}
                          />
                        ) : (
                          <SalesDropzone
                            instructions={'Adjuntar Documentos (opcional)'}
                            maxWeight={'(JPG, PNG, PDF, máx. 3MB)'}
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            message="Arrastrar o buscar Documento"
                          />
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Tipo de Documento *'}
                    type={'select'}
                    name={'documentTypeImage'}
                    register={register}
                    capitalizeOff={true}
                    placeholder={''}
                    options={idDocumentTypeOptions}
                    errors={errors?.documentTypeImage?.message}
                  />
                  <div className="col-6"></div>

                  {validateIfshowForm() && (
                    <div div className="col-12 col-md-6">
                      {idUploaded ? (
                        <FileComponent
                          file={idUploaded}
                          remove={() => setIdUploaded('')}
                        />
                      ) : (
                        <SalesDropzone
                          instructions={'Adjuntar Cédula de identidad / RIF (opcional)'}
                          maxWeight={'(JPG, PNG, PDF, máx. 3MB)'}
                          getRootProps={getRootProps}
                          getInputProps={getInputProps}
                          message="Arrastrar o buscar Documento"
                        />
                      )}
                    </div>
                  )}
                </>
              )}

              {(validateIfshowForm() || showUploader()) && (
                <>
                  <Input
                    bootstrap={'col-4 col-sm-2 col-md-2'}
                    label={'C.I / RIF*'}
                    type={'select'}
                    name={'identificationType'}
                    register={register}
                    placeholder={''}
                    options={idCodes}
                    errors={errors?.identificationType?.message}
                    onChange={() => setValue('identification', '')}
                  />
                  {[idEnums?.J, idEnums?.E, idEnums?.P]?.includes(
                    watch('identificationType')
                  ) && (
                    <Input
                      bootstrap={'col-8 col-sm-4 col-md-4'}
                      label={''}
                      type={'text'}
                      name={'identification'}
                      register={register}
                      placeholder={''}
                      errors={errors?.identification?.message}
                      onChange={getCustomerById}
                      numpadForMobile={true}
                    />
                  )}
                  {![idEnums?.J, idEnums?.E, idEnums?.P]?.includes(
                    watch('identificationType')
                  ) && (
                    <Input
                      bootstrap={'col-8 col-sm-4 col-md-4'}
                      label={''}
                      type={'number'}
                      name={'identification'}
                      register={register}
                      placeholder={''}
                      errors={errors?.identification?.message}
                      onChange={getCustomerById}
                      numpadForMobile={true}
                    />
                  )}
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Nombre *'}
                    type={'text'}
                    name={'firstName'}
                    register={register}
                    placeholder={''}
                    errors={errors?.firstName?.message}
                    disabled={validateDisabled()}
                  />
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Apellido *'}
                    type={'text'}
                    name={'lastName'}
                    register={register}
                    placeholder={''}
                    errors={errors?.lastName?.message}
                    disabled={validateDisabled()}
                  />
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={`Fecha de Nacimiento DD/MM/AAAA ${
                      ![idEnums?.J, idEnums?.R, idEnums?.G].includes(
                        watch('identificationType')
                      )
                        ? '*'
                        : ''
                    } `}
                    type={'date'}
                    name={'birthday'}
                    register={register}
                    control={control}
                    errors={errors?.birthday?.message}
                    disabled={validateDisabled()}
                  />
                  <Input
                    bootstrap={'col-4 col-sm-3 col-md-3'}
                    label={`Sexo  ${
                      ![idEnums?.J, idEnums?.R, idEnums?.G].includes(
                        watch('identificationType')
                      )
                        ? '*'
                        : ''
                    }`}
                    type={'select'}
                    name={'gender'}
                    register={register}
                    placeholder={''}
                    options={genderOptions}
                    errors={errors?.gender?.message}
                    disabled={validateDisabled()}
                  />{' '}
                  <Input
                    bootstrap={'col-4 col-sm-3 col-md-3'}
                    label={`Estado Civil  ${
                      ![idEnums?.J, idEnums?.R, idEnums?.G].includes(
                        watch('identificationType')
                      )
                        ? '*'
                        : ''
                    }`}
                    type={'select'}
                    name={'maritalStatus'}
                    register={register}
                    placeholder={''}
                    options={civilStateOptions}
                    errors={errors?.maritalStatus?.message}
                    disabled={validateDisabled()}
                  />
                  <div className="col-12"></div>
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Correo Electrónico *'}
                    type={'text'}
                    name={'email'}
                    register={register}
                    placeholder={''}
                    errors={errors?.email?.message}
                    disabled={validateDisabled()}
                  />
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Correo Secundario'}
                    type={'text'}
                    name={'optionalEmail'}
                    register={register}
                    placeholder={''}
                    errors={errors?.optionalEmail?.message}
                    disabled={validateDisabled()}
                  />
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Teléfono *'}
                    type={'phone'}
                    control={control}
                    name={'phone'}
                    register={register}
                    placeholder={''}
                    errors={errors?.phone?.message}
                    disabled={validateDisabled()}
                    numpadForMobile={true}
                  />
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Teléfono Secundario'}
                    type={'phone'}
                    control={control}
                    name={'optionalPhone'}
                    register={register}
                    placeholder={''}
                    errors={errors?.optionalPhone?.message}
                    disabled={validateDisabled()}
                    numpadForMobile={true}
                  />
                  {/* <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'¿Cómo te enteraste de nosotros? *'}
                    type={'select'}
                    options={broadcastOptions}
                    name={'broadcastChannel'}
                    placeholder={'Seleccione'}
                    register={register}
                    errors={errors?.broadcastChannel?.message}
                    disabled={validateDisabled()}
                    capitalizeOff={true}
                  />{' '} */}
                  {filterOption(broadcastOptions, 'Otro')?._id ===
                    watch('broadcastChannel') && (
                    <Input
                      bootstrap={'col-12 col-md-6'}
                      label={'Otro *'}
                      type={'text'}
                      name={'broadcastChannelDescription'}
                      register={register}
                      errors={errors?.broadcastChannelDescription?.message}
                    />
                  )}
                  <Input
                    bootstrap={'col-12 col-md-6'}
                    label={'Instagram'}
                    type={'text'}
                    name={'instagram'}
                    placeholder={''}
                    register={register}
                    errors={errors?.instagram?.message}
                  />{' '}
                  <col className="md-6" />
                </>
              )}
            </div>
          </form>
        )}
        <div
          style={{ margin: '24px 0px 48px 0px' }}
          className="buttons-center-container"
        >
          <AltButton
            label={'Regresar'}
            action={() => setcurentStep(curentStep - 1)}
          />
          {validateButton()}
          {validateContinueButton()}
        </div>
      </div>
    </>
  );
});

const SameClientComponent = (props) => {
  const { sameClient, differentClient, isSameClient } = props;
  return (
    <div className="same-client-component">
      {isSameClient && (
        <img
          src={checkboxChecked}
          alt="checkbox"
          onClick={() => differentClient()}
        />
      )}
      {!isSameClient && (
        <img src={checkbox} alt="checkbox" onClick={() => sameClient()} />
      )}
      <h6>Mismo cliente para todos los vehículos</h6>
    </div>
  );
}
